import dayjs from 'dayjs';
import {makeAutoObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {PaymentExpandedCommon} from '../../../../api/payment';
import type {Refund} from './types';

import {datetimeObjToISOString} from '../../../../utils/datetime';
import {isPaymentFailed} from '../../../../utils/entity/payment';
import {Currency, getPriceString} from '../../../../utils/money';
import {getAvailableAmountToRefund} from '../../stores/MakeRefund/utils';

export class RefundModel {
  payment: PaymentExpandedCommon;
  refund: Refund;

  constructor(refund: Refund, payment: PaymentExpandedCommon) {
    this.refund = refund;
    this.payment = payment;

    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  get amountString() {
    return getPriceString({
      currency: this.currency,
      price: this.refund?.amount || 0,
    });
  }

  get currency() {
    const FALLBACK_CURRENCY_KEY: keyof typeof Currency = 'USD';

    const currencyKey = this.payment?.currency || FALLBACK_CURRENCY_KEY;

    return Currency[currencyKey];
  }

  get date() {
    if (this.status) {
      return this.refund?.start_date;
    }

    return this.refund?.end_date;
  }

  get dateString() {
    return dayjs(datetimeObjToISOString(this.date)).format('lll');
  }

  get description() {
    return this.refund?.description || '';
  }

  get isPartial() {
    const {byCard, byStripe} = getAvailableAmountToRefund(this.payment);

    const compareTo = this.isRefundByStripe ? byStripe : byCard;

    return (this.refund?.amount || 0) <= compareTo;
  }

  get isRefundByStripe() {
    return !this.refund?.refund_payment_id;
  }

  get isRefundPaymentFailed() {
    if (this.refund?.refundPayment) {
      return isPaymentFailed(this.refund.refundPayment);
    }

    return this.status === 'failed';
  }

  get isRetryAvailable() {
    const isActionRequired =
      this.refund?.refundPayment?.status === 'requires_action';

    return (isActionRequired || this.isRefundPaymentFailed) && this.isPartial;
  }

  get status() {
    return this.refund?.status;
  }

  get statusString() {
    if (this.refund?.refundPayment) {
      if (this.refund?.refundPayment.status === 'requires_action') {
        return i18n.t('shared:card_action_required');
      } else if (this.isRefundPaymentFailed) {
        return i18n.t('shared:failed');
      }
    }

    if (this.status === 'complete') {
      return i18n.t('shared:success');
    } else if (this.status === 'failed') {
      return i18n.t('shared:failed');
    }

    return i18n.t('shared:in_progress');
  }

  get tax() {
    if (this.isRefundByStripe) {
      return 0;
    } else {
      // tax is only possible for refunds by a card
      // such refunds have their own payments
      return this.refund?.refundPayment
        ? this.refund.refundPayment.amount - this.refund.amount
        : 0;
    }
  }

  get taxString() {
    const string = getPriceString({
      currency: this.currency,
      price: this.tax,
    });

    return this.tax ? `+${string}` : '';
  }

  get variant() {
    if (this.isRefundPaymentFailed) {
      return 'failed';
    }

    if (this.status === 'complete') {
      return 'success';
    } else if (this.status === 'failed') {
      return 'failed';
    }
  }
}
